import { db } from '../services/firebase';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';

/**
 * Sistema automático de detección y configuración de administradores
 * Este sistema detecta automáticamente si un usuario que inicia sesión es administrador
 * y configura su rol correctamente en la base de datos
 */

/**
 * Verifica si un usuario tiene rol 'admin' y actualiza su rol si es necesario
 * @param {Object} user - Objeto de usuario de Firebase Auth
 * @returns {Promise<boolean>} - true si tiene rol admin, false si no
 */
export const autoDetectAndConfigureAdmin = async (user) => {
  if (!user || !user.uid) {
    console.log('❌ No hay usuario para verificar');
    return false;
  }

  try {
    console.log(`🔍 Verificando automáticamente si el usuario ${user.uid} tiene rol admin...`);
    
    // Verificar si el usuario existe en la base de datos
    const userRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      
      // Verificar si ya tiene rol admin
      if (userData.role === 'admin' || userData.isAdmin === true) {
        console.log(`✅ Usuario ${user.uid} ya tiene rol admin`);
        
        // Asegurar que todos los campos de administrador estén configurados correctamente
        if (userData.role !== 'admin' || userData.isAdmin !== true) {
          await updateDoc(userRef, {
            role: 'admin',
            isAdmin: true,
            updatedAt: new Date()
          });
          console.log(`🔄 Actualizado rol admin para ${user.uid}`);
        }
        
        return true;
      }
      
      // Si el usuario es un admin conocido, configurarlo automáticamente
      const knownAdmins = [
        'admin@paradise.com',
        'admin@theparadisellc.com',
        'admin@paradiselandscaping.com'
      ];
      
      if (user.email && knownAdmins.includes(user.email.toLowerCase())) {
        console.log(`🔑 Usuario ${user.email} reconocido como admin conocido`);
        await updateDoc(userRef, {
          role: 'admin',
          isAdmin: true,
          updatedAt: new Date()
        });
        return true;
      }
      
      // Si el usuario tiene un dominio de correo admin
      if (user.email && (
        user.email.endsWith('@paradise.com') || 
        user.email.endsWith('@theparadisellc.com') || 
        user.email.endsWith('@paradiselandscaping.com')
      )) {
        console.log(`🔑 Usuario ${user.email} tiene dominio de correo admin`);
        await updateDoc(userRef, {
          role: 'admin',
          isAdmin: true,
          updatedAt: new Date()
        });
        return true;
      }
      
      console.log(`ℹ️ Usuario ${user.uid} no tiene rol admin`);
      return false;
    } else {
      console.log(`⚠️ Usuario ${user.uid} no existe en la base de datos`);
      
      // Si el usuario no existe pero tiene un correo admin conocido, crearlo como admin
      if (user.email && (
        user.email === 'admin@paradise.com' || 
        user.email === 'admin@theparadisellc.com' || 
        user.email === 'admin@paradiselandscaping.com'
      )) {
        console.log(`🔑 Creando usuario ${user.email} con rol admin`);
        await setDoc(userRef, {
          email: user.email,
          displayName: user.displayName || 'Admin',
          role: 'admin',
          isAdmin: true,
          createdAt: new Date(),
          updatedAt: new Date()
        });
        return true;
      }
      
      return false;
    }
  } catch (error) {
    console.error('❌ Error verificando administrador:', error);
    return false;
  }
};

/**
 * Configura automáticamente a un usuario con rol admin
 * @param {Object} user - Objeto de usuario de Firebase Auth
 * @returns {Promise<boolean>} - true si se configuró correctamente, false si no
 */
export const autoConfigureAsAdmin = async (user) => {
  if (!user || !user.uid) {
    console.error('❌ No hay usuario para configurar');
    return false;
  }
  
  try {
    console.log(`🔧 Configurando automáticamente a ${user.uid} con rol admin...`);
    
    const userRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      // Actualizar usuario existente
      await updateDoc(userRef, {
        role: 'admin',
        isAdmin: true,
        updatedAt: new Date()
      });
    } else {
      // Crear nuevo usuario
      await setDoc(userRef, {
        email: user.email || `admin-${user.uid.substring(0, 6)}@paradise.com`,
        displayName: user.displayName || 'Admin',
        role: 'admin',
        isAdmin: true,
        createdAt: new Date(),
        updatedAt: new Date()
      });
    }
    
    console.log(`✅ Usuario ${user.uid} configurado con rol admin`);
    return true;
  } catch (error) {
    console.error('❌ Error configurando administrador:', error);
    return false;
  }
};

// Exportar funciones para uso global
window.autoDetectAndConfigureAdmin = autoDetectAndConfigureAdmin;
window.autoConfigureAsAdmin = autoConfigureAsAdmin;
